import { useEffect, useState, useRef } from "react";
import { documentAPI, documentTypeAPI, lookupAPI, approvalAPI } from "../services/api";
import { StatusBadge, Modal, Btn, Th, Td, ActionBtns, ThActions, ConfirmDialog, Toast, Can, errMessage, TableScroll, Pagination } from "../components/shared/index";
import { useLanguage } from "../context/LanguageContext";
import { MdSearch, MdWarning, MdLightbulb, MdSend, MdEdit, MdDelete, MdCreate, MdVisibility, MdCheckCircle, MdCancel, MdFolder, MdDescription, MdAttachFile, MdFileDownload } from "react-icons/md";

const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const DOC_STATUS_FALLBACK = {
  VALID:   "ចូលប្រើបាន",
  EXPIRED: "ផុតកំណត់",
  PENDING: "ត្រូវផ្ទៀងផ្ទាត់",
  REVOKED: "បោះបង់",
};
const TYPE_STATUS = { ACTIVE:"សកម្ម", INACTIVE:"មិនសកម្ម" };

const inputStyle = {
  width:"100%", padding:"9px 12px",
  border:`1px solid ${BORDER}`, borderRadius:8,
  fontSize:13, outline:"none", background:"var(--surface)",
  fontFamily:KH, boxSizing:"border-box",
};
const errStyle = (key, errors) => errors[key]
  ? { ...inputStyle, border:"1.5px solid #EF4444", background:"#FFF5F5" }
  : inputStyle;

const toDisplay = (iso) => {
  if (!iso) return "—";
  const p = iso.slice(0,10).split("-");
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : iso;
};

function SkeletonRow({ cols }) {
  return (
    <tr>
      {Array(cols).fill(0).map((_,i) => (
        <td key={i} style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
          <div style={{ height:14, background:"var(--border)", borderRadius:4 }} />
        </td>
      ))}
    </tr>
  );
}

function ErrMsg({ field, errors }) {
  if (!errors[field]) return null;
  return (
    <p style={{ fontSize:11, color:"#EF4444", marginTop:3, fontFamily:KH, display:"flex", alignItems:"center", gap:3 }}>
      <MdWarning size={13} /> {errors[field]}
    </p>
  );
}

function SectionTitle({ children }) {
  return (
    <p style={{ fontSize:11, color:MUTED, fontWeight:600,
                margin:"14px 0 8px", borderBottom:`1px solid ${BORDER}`,
                paddingBottom:4, fontFamily:KH }}>
      {children}
    </p>
  );
}

function Label({ children, required }) {
  return (
    <label style={{ display:"block", fontSize:12, color:MUTED,
                    marginBottom:4, fontFamily:KH }}>
      {children}
      {required && <span style={{ color:"#EF4444", marginLeft:3 }}>*</span>}
    </label>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display:"flex", padding:"8px 0",
                  borderBottom:`1px solid ${BORDER}` }}>
      <span style={{ fontSize:12, color:MUTED, minWidth:160,
                     fontFamily:KH, flexShrink:0 }}>{label}</span>
      <span style={{ fontSize:13, color:"var(--text)",
                     fontFamily:KH }}>{value || "—"}</span>
    </div>
  );
}

function OfficerDocuments({ docTypes, docStatuses }) {
  const { t } = useLanguage();
  const getDocLabel = (code) =>
    docStatuses.find(s => s.statusCode === code)?.labelKh
    || DOC_STATUS_FALLBACK[code] || code || "—";

  const [list,       setList]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [statusFil,  setStatusFil]  = useState("");
  const [modal,      setModal]      = useState(null);
  const [form,       setForm]       = useState({});
  const [selected,   setSelected]   = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [errors,     setErrors]     = useState({});
  const [page,       setPage]       = useState(0);
  const [pageSize,   setPageSize]   = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteId,   setDeleteId]   = useState(null);
  const [toast,      setToast]      = useState(null);
  const [err,        setErr]        = useState(null);
  const [docFile,    setDocFile]    = useState(null);
  const [attUrl,     setAttUrl]     = useState(null);

  const docTypeRef    = useRef(null);
  const docNameRef    = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res  = await documentAPI.getAll(page, pageSize, undefined, undefined, statusFil || undefined);
      const data = res?.data?.data ?? res?.data;
      setList(data?.content || []);
      setTotalPages(data?.totalPages    || 1);
      setTotalItems(data?.totalElements || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, pageSize, statusFil]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    const reqFields = [
      { key:"documentTypeId", msg:t("សូមជ្រើសរើសប្រភេទឯកសារ"), ref:docTypeRef, empty:!form.documentTypeId },
      { key:"documentName",   msg:t("សូមបញ្ចូលឈ្មោះឯកសារ"),   ref:docNameRef, empty:!form.documentName?.trim() },
    ];
    const reqErrors = {};
    reqFields.forEach(f => { if (f.empty) reqErrors[f.key] = f.msg; });
    if (Object.keys(reqErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...reqErrors }));
      const first = reqFields.find(f => f.empty && f.ref?.current);
      if (first) setTimeout(() => first.ref.current.focus(), 80);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        documentTypeId: form.documentTypeId ? parseInt(form.documentTypeId, 10) : null,
        documentName:   form.documentName   || "",
        documentNumber: form.documentNumber || "",
        note:           form.note           || "",
        expiryDate:     form.expiryDate     || null,
      };
      let savedId = editId;
      if (editId) {
        await documentAPI.update(editId, payload);
      } else {
        const res = await documentAPI.create(payload);
        const created = res?.data?.data ?? res?.data;
        savedId = created?.documentId ?? created?.id;
      }

      if (docFile && savedId) {
        try {
          await documentAPI.uploadAttachment(savedId, docFile);
        } catch (upErr) {
          console.error("Attachment upload failed:", upErr);
          setErr("រក្សាទុកឯកសាររួចហើយ ប៉ុន្តែការភ្ជាប់ឯកសារបរាជ័យ: " + errMessage(upErr));
        }
      }

      await load();
      setToast(editId ? "កែប្រែឯកសារបានជោគជ័យ!" : "បន្ថែមឯកសារបានជោគជ័យ!");
      setModal(null); setForm({}); setEditId(null); setErrors({}); setDocFile(null);
    } catch (e) {
      try {
        const body = e.body || JSON.parse(e.message);
        if (body?.data) { setErrors(body.data); return; }
      } catch {}
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => setDeleteId(id);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await documentAPI.delete(id);
      await load();
      if ((selected?.documentId ?? selected?.id) === id) setModal(null);
      setToast(t("លុបឯកសារបានជោគជ័យ!"));
    } catch (e) { setErr(errMessage(e)); }
  };

  const openEdit = (d) => {
    const id = d.documentId ?? d.id;
    setEditId(id);
    setErrors({});
    setDocFile(null);
    setForm({
      documentTypeId: d.documentTypeId ? String(d.documentTypeId) : "",
      documentName:   d.documentName   || "",
      documentNumber: d.documentNumber || "",
      note:           d.note           || "",
      expiryDate:     d.expiryDate?.slice(0,10) || "",
      _hasFile:       !!d.fileUrl,
    });
    setModal("add");
  };

  const openView = (d) => {
    setSelected(d);
    setAttUrl(null);
    setModal("view");
    if (d.fileUrl) {
      documentAPI.getDownloadUrl(d.documentId ?? d.id)
        .then(r => {
          const u = r?.data?.data ?? r?.data;
          if (typeof u === "string" && u) setAttUrl(u);
        })
        .catch(() => {});
    }
  };

  const attName = (d) => {
    const u = d?.fileUrl;
    if (typeof u !== "string" || !u) return null;
    try {
      const seg = decodeURIComponent(u.split("?")[0].split("/").pop() || "");
      return seg || null;
    } catch { return null; }
  };

  const filtered = list.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [d.documentName, d.documentNumber, d.userName, d.documentTypeName]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)",
                    gap:12, marginBottom:20 }}>
        {[
          { label:"ឯកសារសរុប",              value:totalItems,                                color:BROWN   },
          { label:"ចូលប្រើបាន",              value:list.filter(d=>(d.statusCode||d.status)==="VALID").length,   color:"#10B981" },
          { label:"ត្រូវផ្ទៀងផ្ទាត់",        value:list.filter(d=>(d.statusCode||d.status)==="PENDING").length, color:"#F59E0B" },
          { label:"ផុតកំណត់",               value:list.filter(d=>(d.statusCode||d.status)==="EXPIRED").length,  color:"#EF4444" },
        ].map((s,i) => (
          <div key={i} style={{ background:"var(--surface)", borderRadius:10,
                                 padding:"14px 18px", border:`1px solid ${BORDER}` }}>
            <p style={{ margin:"0 0 6px", fontSize:12, color:MUTED, fontFamily:KH }}>{s.label}</p>
            <p style={{ margin:0, fontSize:26, fontWeight:700,
                        color:s.color, fontFamily:MO }}>
              {loading ? "..." : s.value}
            </p>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center" }}>
        <div style={{ flex:1, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`,
                      borderRadius:8, padding:"0 12px" }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t("ស្វែងរកឈ្មោះ ឬ លេខឯកសារ...")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>
        <select value={statusFil} onChange={e => { setStatusFil(e.target.value); setPage(0); }}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("ស្ថានភាពទាំងអស់")}</option>
          {docStatuses.map(s => (
            <option key={s.statusCode} value={s.statusCode}>
              {s.labelKh || s.labelEn || s.statusCode}
            </option>
          ))}
        </select>
        <Can perm="document" action="create">
        <button onClick={() => { setEditId(null); setErrors({}); setForm({}); setDocFile(null); setModal("add"); }}
          style={{ padding:"9px 18px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, whiteSpace:"nowrap", fontFamily:KH }}>
          {t("+ បន្ថែមឯកសារ")}
        </button>
        </Can>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("ឈ្មោះឯកសារ")}</Th>
              <Th>{t("លេខឯកសារ")}</Th>
              <Th>{t("ម្ចាស់")}</Th>
              <Th>{t("ប្រភេទ")}</Th>
              <Th>{t("ស្ថានភាព")}</Th>
              <Th>{t("ថ្ងៃផុតកំណត់")}</Th>
              <Th>{t("ថ្ងៃបញ្ចូល")}</Th>
              <ThActions perm="document" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4].map(i => <SkeletonRow key={i} cols={8} />)
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding:"32px", textAlign:"center",
                                          color:MUTED, fontSize:13, fontFamily:KH }}>
                  {t("គ្មានឯកសារ")}
                </td>
              </tr>
            ) : filtered.map(d => (
              <tr key={d.documentId ?? d.id}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                <Td>{d.documentName || "—"}</Td>
                <Td muted>{d.documentNumber || "—"}</Td>
                <Td>{d.userName || d.departmentName || "—"}</Td>
                <Td>{d.documentTypeName || "—"}</Td>
                <Td>
                  <StatusBadge status={getDocLabel(d.statusCode || d.status)} />
                </Td>
                <Td muted style={{ color: (() => {
                  if (!d.expiryDate) return MUTED;
                  const days = Math.ceil((new Date(d.expiryDate)-new Date())/(1000*60*60*24));
                  return days <= 30 ? "#F59E0B" : MUTED;
                })() }}>
                  {toDisplay(d.expiryDate)}
                </Td>
                <Td muted>{toDisplay(d.createdAt)}</Td>
                <ActionBtns perm="document"
                  onView={() => openView(d)}
                  onEdit={() => openEdit(d)}
                  onDelete={() => handleDelete(d.documentId ?? d.id)}
                />
              </tr>
            ))}
          </tbody>
        </TableScroll>
        <Pagination
          page={page} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      {modal === "add" && (
        <Modal
          key={editId || "new-doc"}
          title={editId ? "កែប្រែឯកសារ" : "បន្ថែមឯកសារថ្មី"}
          onClose={() => { setModal(null); setForm({}); setEditId(null); setErrors({}); setDocFile(null); }}
        >
          <SectionTitle>{t("ព័ត៌មានឯកសារ")}</SectionTitle>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <div style={{ marginBottom:14 }}>
              <Label required>{t("ប្រភេទឯកសារ")}</Label>
              <select ref={docTypeRef} value={form.documentTypeId||""}
                onChange={e => { f("documentTypeId",e.target.value); setErrors(p=>({...p,documentTypeId:undefined})); }}
                style={errStyle("documentTypeId", errors)}>
                <option value="">{t("ជ្រើសរើសប្រភេទ...")}</option>
                {docTypes.map(dt => (
                  <option key={dt.documentTypeId ?? dt.id}
                    value={String(dt.documentTypeId ?? dt.id)}>
                    {dt.documentTypeName || dt.name}
                  </option>
                ))}
              </select>
              <ErrMsg field="documentTypeId" errors={errors} />
            </div>
            <div style={{ marginBottom:14 }}>
              <Label required>{t("ឈ្មោះឯកសារ")}</Label>
              <input ref={docNameRef} value={form.documentName||""}
                onChange={e => { f("documentName",e.target.value); setErrors(p=>({...p,documentName:undefined})); }}
                placeholder={t("ឈ្មោះពេញឯកសារ")}
                style={errStyle("documentName", errors)} />
              <ErrMsg field="documentName" errors={errors} />
            </div>
            <div style={{ marginBottom:14 }}>
              <Label>{t("លេខឯកសារ")}</Label>
              <input value={form.documentNumber||""}
                onChange={e => f("documentNumber",e.target.value)}
                placeholder="DOC-XXXX" style={inputStyle} />
            </div>
            <div style={{ marginBottom:14 }}>
              <Label>{t("ថ្ងៃផុតកំណត់")}</Label>
              <input type="date" value={form.expiryDate||""}
                onChange={e => f("expiryDate",e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <Label>{t("កំណត់ត្រា")}</Label>
            <textarea value={form.note||""}
              onChange={e => f("note",e.target.value)}
              rows={3} placeholder={t("ព័ត៌មានបន្ថែម ឬ កំណត់សម្គាល់...")}
              style={{ ...inputStyle, resize:"vertical" }} />
          </div>

          <div style={{ marginBottom:14 }}>
            <Label><span style={{ display:"inline-flex", alignItems:"center", gap:4 }}><MdAttachFile size={14}/> {t("ឯកសារភ្ជាប់")}</span></Label>

            {!docFile && form._hasFile && (
              <div style={{ display:"flex", alignItems:"center", gap:8,
                            padding:"8px 12px", background:"#EDE9FE",
                            borderRadius:8, marginBottom:8 }}>
                <MdDescription size={16} style={{ color:"#5B21B6", flexShrink:0 }}/>
                <span style={{ fontSize:12, color:"#5B21B6", fontFamily:KH }}>
                  {t("មានឯកសារភ្ជាប់រួចហើយ")}
                </span>
                <span style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                  {t("— ជ្រើសរើសឯកសារថ្មីដើម្បីជំនួស")}
                </span>
              </div>
            )}

            <label style={{
              display:"flex", alignItems:"center", gap:12,
              padding:"14px 16px", border:`2px dashed ${BORDER}`,
              borderRadius:8, background: docFile ? "#F0FDF4" : BG,
              cursor:"pointer", transition:"all 0.2s",
            }}>
              <span style={{ fontSize:28, display:"flex", alignItems:"center", color:MUTED }}>
                {docFile ? <MdCheckCircle size={28} color="#10B981"/> : <MdFolder size={28}/>}
              </span>
              <div style={{ flex:1 }}>
                {docFile ? (
                  <div>
                    <p style={{ fontSize:13, fontWeight:600, color:"#065F46",
                                fontFamily:KH, marginBottom:2 }}>
                      {docFile.name}
                    </p>
                    <p style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                      {(docFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                ) : (
                  <div>
                    <p style={{ fontSize:13, color:BROWN, fontFamily:KH, marginBottom:2 }}>
                      {t("ចុចដើម្បីជ្រើសរើសឯកសារ")}
                    </p>
                    <p style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                      PDF, Word, Excel, JPG, PNG
                    </p>
                  </div>
                )}
              </div>
              {docFile && (
                <button onClick={e => { e.preventDefault(); setDocFile(null); }}
                  style={{ background:"#FEE2E2", border:"none", borderRadius:6,
                           padding:"4px 8px", cursor:"pointer",
                           color:"#991B1B", fontSize:12 }}>
                  {t("លុប")}
                </button>
              )}
              <input type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                onChange={e => setDocFile(e.target.files[0] || null)}
                style={{ display:"none" }} />
            </label>
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setForm({}); setEditId(null); setErrors({}); setDocFile(null); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSave} style={{ opacity:saving ? 0.7 : 1 }}>
              {saving ? "កំពុង..." : "រក្សាទុក"}
            </Btn>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        open={deleteId !== null}
        message={t("តើអ្នកពិតជាចង់លុបឯកសារនេះមែនទេ?")}
        onConfirm={doDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {modal === "view" && selected && (
        <Modal
          title={selected.documentName || "ឯកសារ"}
          subtitle={<StatusBadge status={getDocLabel(selected.statusCode || selected.status)} />}
          onClose={() => setModal(null)}
        >
          <SectionTitle>{t("ព័ត៌មានសង្ខេប")}</SectionTitle>
          <InfoRow label={t("ឈ្មោះឯកសារ")}value={selected.documentName} />
          <InfoRow label={t("ប្រភេទឯកសារ")}  value={selected.documentTypeName} />
          <InfoRow label={t("លេខឯកសារ")} value={selected.documentNumber} />
          
          <InfoRow label={t("អ្នកបញ្ជូលឯកសារ")}value={selected.userName || selected.userId} />

          <SectionTitle>{t("ថ្ងៃ និងស្ថានភាព")}</SectionTitle>
          <InfoRow label={t("ថ្ងៃផុតកំណត់")}  value={toDisplay(selected.expiryDate)} />
          <InfoRow label={t("ថ្ងៃបង្កើត")}   value={toDisplay(selected.createdAt)} />
          <InfoRow label={t("ថ្ងៃកែប្រែ")}   value={toDisplay(selected.updatedAt)} />

          {selected.note && (
            <>
              <SectionTitle>{t("កំណត់ត្រា")}</SectionTitle>
              <p style={{ fontSize:13, color:"var(--text)", fontFamily:KH,
                          lineHeight:1.7, whiteSpace:"pre-wrap" }}>
                {selected.note}
              </p>
            </>
          )}

          {selected.fileUrl && (
            <>
              <SectionTitle><span style={{ display:"inline-flex", alignItems:"center", gap:4 }}><MdAttachFile size={14}/> {t("ឯកសារភ្ជាប់")}</span></SectionTitle>
              {attUrl ? (() => {
                const probe = `${attName(selected) || ""} ${attUrl}`;
                const isImg = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|#|$|\s)/i.test(probe);
                return isImg ? (
                  <div style={{ textAlign:"center", marginBottom:8 }}>
                    <img src={attUrl} alt="attachment"
                      style={{ maxWidth:"100%", maxHeight:280,
                               borderRadius:8, border:`1px solid ${BORDER}`,
                               objectFit:"contain" }} />
                    <a href={attUrl} target="_blank" rel="noreferrer"
                      style={{ display:"inline-flex", alignItems:"center", gap:4, marginTop:8,
                               fontSize:12, color:BROWN, fontFamily:KH }}>
                      <MdFileDownload size={15}/> {t("ទាញយករូបភាព")}
                    </a>
                  </div>
                ) : (
                  <a href={attUrl} target="_blank" rel="noreferrer"
                    style={{ display:"flex", alignItems:"center", gap:10,
                             padding:"12px 14px", background:"#EDE9FE",
                             borderRadius:8, textDecoration:"none",
                             marginBottom:8 }}>
                    <MdDescription size={24} style={{ color:"#5B21B6", flexShrink:0 }}/>
                    <div>
                      <p style={{ fontSize:12, color:"#5B21B6", fontFamily:KH,
                                  fontWeight:600, marginBottom:2 }}>
                        {attName(selected) || "ឯកសារភ្ជាប់"}
                      </p>
                      <p style={{ fontSize:11, color:MUTED, fontFamily:KH,
                                  display:"flex", alignItems:"center", gap:4 }}>
                        {t("ចុចដើម្បីបើក / ទាញយក")} <MdFileDownload size={13}/>
                      </p>
                    </div>
                  </a>
                );
              })() : (
                <p style={{ fontSize:12, color:MUTED, fontFamily:KH,
                            padding:"8px 0" }}>
                  {t("កំពុងផ្ទុក URL...")}
                </p>
              )}
            </>
          )}

          <div style={{ marginTop:20, display:"flex", justifyContent:"space-between" }}>
            <Can perm="document" action="edit">
              <Btn variant="outline" onClick={() => openEdit(selected)} style={{display:"flex",alignItems:"center",gap:5}}>
                <MdEdit size={14}/> {t("កែប្រែ")}
              </Btn>
            </Can>
            <Can perm="document" action="delete">
              <Btn variant="danger"
                onClick={() => handleDelete(selected.documentId ?? selected.id)}
                style={{display:"flex",alignItems:"center",gap:5}}>
                <MdDelete size={14}/> {t("លុប")}
              </Btn>
            </Can>
          </div>
        </Modal>
      )}
    </div>
  );
}

function DocumentTypes({ docTypes, loading, reload }) {
  const { t } = useLanguage();
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState({});
  const [saving,   setSaving]   = useState(false);
  const [editId,   setEditId]   = useState(null);
  const [errors,   setErrors]   = useState({});
  const [search,   setSearch]   = useState("");
  const [filter,   setFilter]   = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [toast,    setToast]    = useState(null);
  const [selected, setSelected] = useState(null);
  const [page,     setPage]     = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    const reqErrors = {};
    if (!form.documentTypeCode?.trim()) reqErrors.documentTypeCode = "សូមបញ្ចូលកូដ";
    if (!form.documentTypeName?.trim()) reqErrors.documentTypeName = "សូមបញ្ចូលឈ្មោះ";
    if (Object.keys(reqErrors).length > 0) {
      setErrors(reqErrors);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        documentTypeCode: form.documentTypeCode || "",
        documentTypeName: form.documentTypeName || "",
        description:      form.description      || "",
        status:           form.status           || "ACTIVE",
      };
      if (editId) {
        await documentTypeAPI.update(editId, payload);
      } else {
        await documentTypeAPI.create(payload);
      }
      await reload();
      setToast({ msg: editId ? "កែប្រែប្រភេទឯកសារបានជោគជ័យ!" : "បន្ថែមប្រភេទឯកសារបានជោគជ័យ!", type:"success" });
      setModal(null); setForm({}); setEditId(null); setErrors({});
    } catch (e) {
      setToast({ msg: errMessage(e), type:"error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => setDeleteId(id);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await documentTypeAPI.delete(id);
      await reload();
      if ((selected?.documentTypeId ?? selected?.id) === id) { setModal(null); setSelected(null); }
      setToast({ msg:t("លុបប្រភេទឯកសារបានជោគជ័យ!"), type:"success" });
    } catch (e) { setToast({ msg: errMessage(e), type:"error" }); }
  };

  const openEdit = (dt) => {
    const id = dt.documentTypeId ?? dt.id;
    setEditId(id); setErrors({});
    setForm({
      documentTypeCode: dt.documentTypeCode || "",
      documentTypeName: dt.documentTypeName || dt.name || "",
      description:      dt.description      || "",
      status:           dt.status           || "ACTIVE",
    });
    setModal("add");
  };

  const filtered = docTypes.filter(dt => {
    const matchSearch = !search || (dt.documentTypeName||"").toLowerCase().includes(search.toLowerCase());
    const matchFilter = !filter || dt.status === filter;
    return matchSearch && matchFilter;
  });

  useEffect(() => { setPage(0); }, [search, filter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <div>
      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center" }}>
        <div style={{ flex:1, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`,
                      borderRadius:8, padding:"0 12px" }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t("ស្វែងរកប្រភេទ...")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("ស្ថានភាពទាំងអស់")}</option>
          <option value="ACTIVE">{t("សកម្ម")}</option>
          <option value="INACTIVE">{t("មិនសកម្ម")}</option>
        </select>
        <Can perm="document" action="create">
        <button onClick={() => { setEditId(null); setErrors({}); setForm({ status:"ACTIVE" }); setModal("add"); }}
          style={{ padding:"9px 18px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, whiteSpace:"nowrap", fontFamily:KH }}>
          {t("+ បន្ថែមប្រភេទ")}
        </button>
        </Can>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("លេខកូដប្រភេទឯកសារ")}</Th>
              <Th>{t("ឈ្មោះប្រភេទឯកសារ")}</Th>
              <Th>{t("ស្ថានភាព")}</Th>
              <Th>{t("ការពិពណ៌នា")}</Th>
              <Th>{t("ថ្ងៃបង្កើត")}</Th>
              <ThActions perm="document" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3].map(i => <SkeletonRow key={i} cols={6} />)
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding:"32px", textAlign:"center",
                                          color:MUTED, fontSize:13, fontFamily:KH }}>
                  {t("គ្មានប្រភេទ")}
                </td>
              </tr>
            ) : pageItems.map(dt => (
              <tr key={dt.documentTypeId ?? dt.id}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                <Td muted>{dt.documentTypeCode || "—"}</Td>
                <Td>{dt.documentTypeName || dt.name || "—"}</Td>
                <Td>
                  <StatusBadge status={TYPE_STATUS[dt.status] || dt.status} />
                </Td>
                <Td muted>{dt.description || "—"}</Td>
                <Td muted>{toDisplay(dt.createdAt)}</Td>
                <ActionBtns perm="document"
                  onView={() => { setSelected(dt); setModal("view"); }}
                  onEdit={() => openEdit(dt)}
                  onDelete={() => handleDelete(dt.documentTypeId ?? dt.id)}
                />
              </tr>
            ))}
          </tbody>
        </TableScroll>
        <Pagination
          page={page} totalPages={pageCount} totalItems={filtered.length} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        message={t("តើអ្នកពិតជាចង់លុបប្រភេទឯកសារនេះមែនទេ?")}
        onConfirm={doDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Toast message={toast?.msg} type={toast?.type}
        duration={toast?.type === "error" ? 6000 : 3000}
        onHide={() => setToast(null)} />

      {modal === "add" && (
        <Modal
          key={editId || "new-type"}
          title={editId ? "កែប្រែប្រភេទឯកសារ" : "បន្ថែមប្រភេទឯកសារ"}
          onClose={() => { setModal(null); setForm({}); setEditId(null); setErrors({}); }}
        >
          <div style={{ marginBottom:14 }}>
            <Label required>{t("លេខសម្គាល់")}</Label>
            <input value={form.documentTypeCode||""}
              onChange={e => { f("documentTypeCode",e.target.value); setErrors(p=>({...p,documentTypeCode:undefined})); }}
              placeholder={t("ឧ. DM101")}
              style={errStyle("documentTypeCode", errors)} />
            <ErrMsg field="documentTypeCode" errors={errors} />
          </div>
          <div style={{ marginBottom:14 }}>
            <Label required>{t("ឈ្មោះប្រភេទ")}</Label>
            <input value={form.documentTypeName||""}
              onChange={e => { f("documentTypeName",e.target.value); setErrors(p=>({...p,documentTypeName:undefined})); }}
              placeholder={t("ឈ្មោះពេញ")}
              style={errStyle("documentTypeName", errors)} />
            <ErrMsg field="documentTypeName" errors={errors} />
          </div>
          <div style={{ marginBottom:14 }}>
            <Label required>{t("ស្ថានភាព")}</Label>
            <select value={form.status||"ACTIVE"}
              onChange={e => f("status",e.target.value)} style={inputStyle}>
              <option value="ACTIVE">{t("សកម្ម")}</option>
              <option value="INACTIVE">{t("មិនសកម្ម")}</option>
            </select>
          </div>
          <div style={{ marginBottom:14 }}>
            <Label>{t("ការពិពណ៌នា")}</Label>
            <textarea value={form.description||""}
              onChange={e => f("description",e.target.value)}
              rows={3} placeholder={t("ពិពណ៌នា...")}
              style={{ ...inputStyle, resize:"vertical" }} />
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setForm({}); setEditId(null); setErrors({}); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSave} style={{ opacity:saving ? 0.7 : 1 }}>
              {saving ? "កំពុង..." : "រក្សាទុក"}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "view" && selected && (
        <Modal
          title={selected.documentTypeName || selected.name || "ប្រភេទឯកសារ"}
          subtitle={<StatusBadge status={TYPE_STATUS[selected.status] || selected.status} />}
          onClose={() => { setModal(null); setSelected(null); }}
        >
          <SectionTitle>{t("ព័ត៌មានសង្ខេប")}</SectionTitle>
          <InfoRow label={t("លេខកូដប្រភេទឯកសារ")} value={selected.documentTypeCode} />
          <InfoRow label={t("ឈ្មោះប្រភេទឯកសារ")}  value={selected.documentTypeName || selected.name} />
          <InfoRow label={t("ស្ថានភាព")}           value={TYPE_STATUS[selected.status] || selected.status} />

          <SectionTitle>{t("កាលបរិច្ឆេទ")}</SectionTitle>
          <InfoRow label={t("ថ្ងៃបង្កើត")} value={toDisplay(selected.createdAt)} />
          <InfoRow label={t("ថ្ងៃកែប្រែ")} value={toDisplay(selected.updatedAt)} />

          {selected.description && (
            <>
              <SectionTitle>{t("ការពិពណ៌នា")}</SectionTitle>
              <p style={{ fontSize:13, color:"var(--text)", fontFamily:KH,
                          lineHeight:1.7, whiteSpace:"pre-wrap" }}>
                {selected.description}
              </p>
            </>
          )}

          <div style={{ marginTop:20, display:"flex", justifyContent:"space-between" }}>
            <Can perm="document" action="edit">
              <Btn variant="outline" onClick={() => openEdit(selected)} style={{display:"flex",alignItems:"center",gap:5}}>
                <MdEdit size={14}/> {t("កែប្រែ")}
              </Btn>
            </Can>
            <Can perm="document" action="delete">
              <Btn variant="danger"
                onClick={() => handleDelete(selected.documentTypeId ?? selected.id)}
                style={{display:"flex",alignItems:"center",gap:5}}>
                <MdDelete size={14}/> {t("លុប")}
              </Btn>
            </Can>
          </div>
        </Modal>
      )}
    </div>
  );
}

const APPROVAL_STATUS_COLOR = {
  PENDING:  { bg:"#FEF3C7", color:"#92400E" },
  APPROVED: { bg:"#D1FAE5", color:"#065F46" },
  REJECTED: { bg:"#FEE2E2", color:"#991B1B" },
};
const APPROVAL_STATUS_LABEL = {
  PENDING:  "រង់ចាំ",
  APPROVED: "បានអនុម័ត",
  REJECTED: "បដិសេធ",
};

function ApprovalBadge({ status }) {
  const s = APPROVAL_STATUS_COLOR[status] || { bg:"#F3F4F6", color:"#6B7280" };
  const l = APPROVAL_STATUS_LABEL[status] || status || "—";
  return (
    <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11,
                   fontWeight:600, background:s.bg, color:s.color,
                   fontFamily:KH }}>
      {l}
    </span>
  );
}

function Approvals({ docList }) {
  const { t } = useLanguage();
  const [list,       setList]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [statusFil,  setStatusFil]  = useState("");
  const [modal,      setModal]      = useState(null);
  const [selected,   setSelected]   = useState(null);
  const [form,       setForm]       = useState({});
  const [saving,     setSaving]     = useState(false);
  const [errors,     setErrors]     = useState({});
  const [page,       setPage]       = useState(0);
  const [pageSize,   setPageSize]   = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [toast,      setToast]      = useState(null);
  const [err,        setErr]        = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res  = await approvalAPI.getAll(page, pageSize, statusFil || undefined);
      const data = res?.data?.data ?? res?.data;
      setList(data?.content || []);
      setTotalPages(data?.totalPages    || 1);
      setTotalItems(data?.totalElements || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, pageSize, statusFil]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleRequest = async () => {
    if (!form.documentId) {
      setErrors({ documentId:"សូមជ្រើសរើសឯកសារ" }); return;
    }
    setSaving(true);
    try {
      await approvalAPI.request({
        documentId:  parseInt(form.documentId, 10),
        note:        form.note || "",
      });
      await load();
      setToast(t("ស្នើសុំអនុម័តបានជោគជ័យ!"));
      setModal(null); setForm({}); setErrors({});
    } catch (e) {
      try {
        const b = e.body || JSON.parse(e.message);
        if (b?.data) { setErrors(b.data); return; }
      } catch {}
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDecide = async (status) => {
    setSaving(true);
    try {
      await approvalAPI.decide(selected.approvalId ?? selected.id, {
        statusCode: status,
        comment:    form.comment || "",
      });
      await load();
      setToast(status === "APPROVED" ? "បានអនុម័តជោគជ័យ!" : "បានបដិសេធជោគជ័យ!");
      setModal(null); setForm({}); setSelected(null);
    } catch (e) {
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)",
                    gap:12, marginBottom:20 }}>
        {[
          { label:"ការស្នើសុំសរុប",  value:totalItems,                                                        color:BROWN   },
          { label:"រង់ចាំអនុម័ត",    value:list.filter(a=>(a.status||a.statusCode)==="PENDING").length,   color:"#F59E0B" },
          { label:"បានអនុម័ត",       value:list.filter(a=>(a.status||a.statusCode)==="APPROVED").length,  color:"#10B981" },
        ].map((s,i) => (
          <div key={i} style={{ background:"var(--surface)", borderRadius:10,
                                 padding:"14px 18px", border:`1px solid ${BORDER}` }}>
            <p style={{ margin:"0 0 6px", fontSize:12, color:MUTED, fontFamily:KH }}>{s.label}</p>
            <p style={{ margin:0, fontSize:26, fontWeight:700,
                        color:s.color, fontFamily:MO }}>
              {loading ? "..." : s.value}
            </p>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center" }}>
        <select value={statusFil} onChange={e => { setStatusFil(e.target.value); setPage(0); }}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`, borderRadius:8,
                   fontSize:13, outline:"none", background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("ស្ថានភាពទាំងអស់")}</option>
          <option value="PENDING">{t("រង់ចាំ")}</option>
          <option value="APPROVED">{t("បានអនុម័ត")}</option>
          <option value="REJECTED">{t("បដិសេធ")}</option>
        </select>
        <div style={{ flex:1 }} />
        <button onClick={() => { setErrors({}); setForm({}); setModal("request"); }}
          style={{ padding:"9px 18px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, whiteSpace:"nowrap", fontFamily:KH }}>
          {t("+ ស្នើសុំអនុម័ត")}
        </button>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("លេខអនុម័ត")}</Th>
              <Th>{t("ឯកសារ")}</Th>
              <Th>{t("ស្នើដោយ")}</Th>
              <Th>{t("ថ្ងៃស្នើ")}</Th>
              <Th>{t("អ្នកអនុម័ត")}</Th>
              <Th>{t("ស្ថានភាព")}</Th>
              <Th>{t("ថ្ងៃអនុម័ត")}</Th>
              <Th>{t("សកម្មភាព")}</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3].map(i => <SkeletonRow key={i} cols={8} />)
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding:"32px", textAlign:"center",
                                          color:MUTED, fontSize:13, fontFamily:KH }}>
                  {t("គ្មានការស្នើសុំ")}
                </td>
              </tr>
            ) : list.map(a => (
              <tr key={a.approvalId ?? a.id}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                <Td muted>#{a.approvalId ?? a.id}</Td>
                <Td>
                  <div>
                    <p style={{ fontSize:13, color:"var(--text)", fontFamily:KH }}>
                      {a.documentName || `Doc #${a.documentId}`}
                    </p>
                    <p style={{ fontSize:11, color:MUTED, fontFamily:KH }}>
                      ID: {a.documentId}
                    </p>
                  </div>
                </Td>
                <Td>{a.requestedByName || a.requestedBy || "—"}</Td>
                <Td muted>{a.requestedAt
                  ? new Date(a.requestedAt).toLocaleString("en-GB") : "—"}</Td>
                <Td>{a.approvedByName || a.approvedBy || "—"}</Td>
                <Td><ApprovalBadge status={a.status || a.statusCode} /></Td>
                <Td muted>{a.approvedAt
                  ? new Date(a.approvedAt).toLocaleString("en-GB") : "—"}</Td>
                <td style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
                  <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                    <button onClick={() => { setSelected(a); setModal("view"); }}
                      style={{ background:"none", border:"none",
                               cursor:"pointer", color:"#3B82F6",
                               display:"flex", alignItems:"center", padding:"4px" }}>
                      <MdVisibility size={17} />
                    </button>
                    {(a.status || a.statusCode) === "PENDING" && (
                      <button onClick={() => { setSelected(a); setForm({ comment:"" }); setModal("decide"); }}
                        style={{ padding:"4px 10px", background:"#EDE9FE",
                                 border:"1px solid #C4B5FD", borderRadius:6,
                                 cursor:"pointer", fontSize:12, color:"#5B21B6",
                                 fontFamily:KH, display:"flex", alignItems:"center", gap:4 }}>
                        <MdCreate size={13}/> {t("សម្រេច")}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </TableScroll>
        <Pagination
          page={page} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {modal === "request" && (
        <Modal
          title={t("ស្នើសុំការអនុម័ត")}
          subtitle={t("ជ្រើសរើសឯកសារដែលត្រូវការអនុម័ត")}
          onClose={() => { setModal(null); setForm({}); setErrors({}); }}
        >
          <div style={{ marginBottom:16 }}>
            <Label required>{t("ឯកសារ")}</Label>
            <select value={form.documentId||""}
              onChange={e => { f("documentId",e.target.value); setErrors({}); }}
              style={errors.documentId
                ? { ...inputStyle, border:"1.5px solid #EF4444", background:"#FFF5F5" }
                : inputStyle}>
              <option value="">{t("ជ្រើសរើសឯកសារ...")}</option>
              {docList.map(d => (
                <option key={d.documentId ?? d.id} value={String(d.documentId ?? d.id)}>
                  {d.documentName || `Doc #${d.documentId ?? d.id}`}
                  {d.documentNumber ? ` (${d.documentNumber})` : ""}
                </option>
              ))}
            </select>
            {errors.documentId && (
              <p style={{ fontSize:11, color:"#EF4444", marginTop:3, fontFamily:KH, display:"flex", alignItems:"center", gap:3 }}>
                <MdWarning size={13}/> {errors.documentId}
              </p>
            )}
          </div>

          <div style={{ marginBottom:16 }}>
            <Label>{t("កំណត់ត្រា (note)")}</Label>
            <textarea value={form.note||""}
              onChange={e => f("note", e.target.value)}
              rows={3}
              placeholder={t("បញ្ចូលមូលហេតុ ឬ ព័ត៌មានបន្ថែម (ស្រេចចិត្ត)...")}
              style={{ ...inputStyle, resize:"vertical" }} />
          </div>

          <div style={{ background:BG, borderRadius:8, padding:"12px 14px",
                        marginBottom:16 }}>
            <p style={{ fontSize:12, color:MUTED, fontFamily:KH, display:"flex", alignItems:"center", gap:5 }}>
              <MdLightbulb size={14} /> {t("ការស្នើសុំនឹងត្រូវបញ្ជូនទៅប្រធានសម្រាប់អនុម័ត")}
            </p>
          </div>

          <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setForm({}); setErrors({}); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleRequest} style={{ opacity:saving ? 0.7 : 1 }}>
              {saving ? "កំពុង..." : <span style={{display:"flex",alignItems:"center",gap:5}}><MdSend size={14}/> {t("ស្នើសុំ")}</span>}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "decide" && selected && (
        <Modal
          title={t("សម្រេចការអនុម័ត")}
          subtitle={selected.documentName || `ឯកសារ #${selected.documentId}`}
          onClose={() => { setModal(null); setForm({}); setSelected(null); }}
        >
          <div style={{ background:BG, borderRadius:8, padding:"12px 14px",
                        marginBottom:16 }}>
            {[
              { label:"លេខអនុម័ត",   value:`#${selected.approvalId ?? selected.id}` },
              { label:"ឯកសារ",      value:selected.documentName || `Doc #${selected.documentId}` },
              { label:"ដែលបានស្នើ",  value:selected.requestedByName || selected.requestedBy },
              { label:"ថ្ងៃខែ",  value:selected.requestedAt
                  ? new Date(selected.requestedAt).toLocaleString("en-GB") : "—" },
            ].map((r,i) => (
              <div key={i} style={{ display:"flex", gap:10, padding:"6px 0",
                                     borderBottom:`1px solid ${BORDER}` }}>
                <span style={{ fontSize:11, color:MUTED, minWidth:130,
                               fontFamily:KH }}>{r.label}</span>
                <span style={{ fontSize:12, color:"var(--text)", fontFamily:KH }}>
                  {r.value || "—"}
                </span>
              </div>
            ))}
          </div>

          <div style={{ marginBottom:20 }}>
            <Label>{t("មតិយោបល់ (comment)")}</Label>
            <textarea value={form.comment||""}
              onChange={e => f("comment",e.target.value)}
              rows={3}
              placeholder={t("បញ្ចូលមូលហេតុ ឬ មតិបន្ថែម (ស្រេចចិត្ត)...")}
              style={{ ...inputStyle, resize:"vertical" }} />
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <button onClick={() => handleDecide("REJECTED")}
              disabled={saving}
              style={{ padding:"12px", background:"#FEF2F2",
                       border:"1.5px solid #EF4444", borderRadius:8,
                       cursor:saving?"not-allowed":"pointer",
                       fontSize:13, fontWeight:600, color:"#991B1B",
                       fontFamily:KH, opacity:saving ? 0.7 : 1,
                       display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
              <MdCancel size={16}/> {t("បដិសេធ")}
            </button>
            <button onClick={() => handleDecide("APPROVED")}
              disabled={saving}
              style={{ padding:"12px", background:"#F0FDF4",
                       border:"1.5px solid #10B981", borderRadius:8,
                       cursor:saving?"not-allowed":"pointer",
                       fontSize:13, fontWeight:600, color:"#065F46",
                       fontFamily:KH, opacity:saving ? 0.7 : 1,
                       display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
              <MdCheckCircle size={16}/> {t("អនុម័ត")}
            </button>
          </div>
          <div style={{ marginTop:12, textAlign:"center" }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setForm({}); setSelected(null); }}>
              {t("បោះបង់")}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "view" && selected && (
        <Modal
          title={`ការអនុម័ត #${selected.approvalId ?? selected.id}`}
          subtitle={<ApprovalBadge status={selected.status || selected.statusCode} />}
          onClose={() => setModal(null)}
        >
          {[
            { label:"លេខអនុម័ត",   value:`#${selected.approvalId ?? selected.id}` },
            { label:"ឯកសារ",   value:selected.documentName || `#${selected.documentId}` },
            { label:"ដែលបានស្នើ",  value:selected.requestedByName || selected.requestedBy },
            { label:"ថ្ងៃខែ",  value:selected.requestedAt ? new Date(selected.requestedAt).toLocaleString("en-GB") : "—" },
            { label:"ដែលបានអនុម័ត",   value:selected.approvedByName || selected.approvedBy || "—" },
            { label:"ស្ថានភាព",        value:<ApprovalBadge status={selected.status || selected.statusCode} /> },
            { label:"ថ្ងៃខែអនុម័ត",   value:selected.approvedAt ? new Date(selected.approvedAt).toLocaleString("en-GB") : "—" },
            { label:"មតិយោបល់",       value:selected.comment || "—" },
          ].map((r,i) => (
            <div key={i} style={{ display:"flex", gap:10, padding:"10px 0",
                                   borderBottom:`1px solid ${BORDER}` }}>
              <span style={{ fontSize:11, color:MUTED, minWidth:140,
                             fontFamily:KH }}>{r.label}</span>
              <span style={{ fontSize:13, color:"var(--text)", fontFamily:KH }}>
                {r.value || "—"}
              </span>
            </div>
          ))}

          {(selected.status || selected.statusCode) === "PENDING" && (
            <div style={{ marginTop:16 }}>
              <Btn onClick={() => { setForm({ comment:"" }); setModal("decide"); }}
                style={{display:"flex",alignItems:"center",gap:5}}>
                <MdCreate size={14}/> {t("ធ្វើការសម្រេច")}
              </Btn>
            </div>
          )}

          <div style={{ marginTop:12, display:"flex", justifyContent:"flex-end" }}>
            <Btn variant="outline" onClick={() => setModal(null)}>{t("បិទ")}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function Documents({ defaultTab = "docs" }) {
  const [docTypes,  setDocTypes]  = useState([]);
  const [typesLoad, setTypesLoad] = useState(true);
  const [docStatuses, setDocStatuses] = useState([]);
  const [docList,     setDocList]     = useState([]);

  const loadTypes = async () => {
    setTypesLoad(true);
    try {
      const res  = await documentTypeAPI.getAll();
      const data = res?.data?.data ?? res?.data;
      setDocTypes(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setTypesLoad(false); }
  };

  useEffect(() => {
    loadTypes();
    lookupAPI.getDocumentStatuses().then(r => {
      const d = r?.data?.data ?? r?.data;
      setDocStatuses(Array.isArray(d) ? d : []);
    }).catch(() => {});
    documentAPI.getAll(0, 200).then(r => {
      const d = r?.data?.data ?? r?.data;
      setDocList(d?.content || []);
    }).catch(() => {});
  }, []);

  return (
    <div>
      {defaultTab === "docs"     && <OfficerDocuments docTypes={docTypes} docStatuses={docStatuses} />}
      {defaultTab === "types"    && <DocumentTypes docTypes={docTypes} loading={typesLoad} reload={loadTypes} />}
      {defaultTab === "approval" && <Approvals docList={docList} />}
    </div>
  );
}